import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { persistedOverviewAtom } from '../atoms';
import { putPersistence } from '@src/api/persistence';
import { useDebouncedCallback } from 'use-debounce';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { OVERVIEW_PERSISTENCE_KEY } from '../constants';

const DEBOUNCE_MS = 500;

// Singleton to prevent multiple saves across instances
const savePending = {
  pending: false,
  pendingState: null as any, // Queue for changes that arrive during save
};

export const usePersistOverview = () => {
  const [persistedOverview] = useAtom(persistedOverviewAtom);
  const previousSavedRef = useRef(persistedOverview);
  const latestStateRef = useRef(persistedOverview);

  // Always keep latestStateRef in sync with current atom value
  latestStateRef.current = persistedOverview;

  const { mutate: saveMutation, isLoading: isSaving } = useMutation(
    async () => {
      // If a save is already in progress, queue this state for next save
      if (savePending.pending) {
        savePending.pendingState = cloneDeep(latestStateRef.current);
        return null;
      }

      try {
        savePending.pending = true;
        const stateToSave = cloneDeep(latestStateRef.current);

        await putPersistence({
          key: OVERVIEW_PERSISTENCE_KEY,
          configuration: stateToSave,
        });

        previousSavedRef.current = stateToSave;
        return stateToSave;
      } finally {
        savePending.pending = false;

        // If state changed during save, trigger another save
        if (
          savePending.pendingState &&
          !isEqual(savePending.pendingState, previousSavedRef.current)
        ) {
          savePending.pendingState = null;
          // Schedule next save after a short delay
          setTimeout(() => saveMutation(), 100);
        } else {
          savePending.pendingState = null;
        }
      }
    },
    {
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('❌ Error saving overview state to persistence:', error);
        savePending.pending = false;
        savePending.pendingState = null;
      },
    },
  );

  // Create stable debounced save function using use-debounce
  const debouncedSave = useDebouncedCallback(() => saveMutation(), DEBOUNCE_MS, {
    leading: false,
    trailing: true,
  });

  // Memoized string comparison helper
  const hasChanged = useCallback((a, b) => !isEqual(a, b), []);

  // Watch atom changes and save to server when needed
  useEffect(() => {
    if (!hasChanged(persistedOverview, previousSavedRef.current)) {
      return;
    }

    debouncedSave();
  }, [persistedOverview, hasChanged, debouncedSave]);

  // Immediate save API for critical actions that bypasses debouncing
  const saveState = useCallback(() => {
    debouncedSave.cancel();

    if (!hasChanged(persistedOverview, previousSavedRef.current)) {
      return;
    }

    saveMutation();
  }, [saveMutation, persistedOverview, hasChanged, debouncedSave]);

  return {
    isLoading: isSaving,
    saveState,
  };
};
